<template>
  <div class="business-solutions">
    <Hero heading="Electrify America Business Solutions">
      <template slot="image">
        <picture>
          <source
            media="(min-width: 950px )"
            srcset="
              @/assets/images/BusinessSolutions/business-solutions-hero.png,
              @/assets/images/BusinessSolutions/business-solutions-hero@2x.webp 2x
            "
          />
          <source
            media="(min-width: 576px )"
            srcset="
              @/assets/images/BusinessSolutions/business-solutions-hero-tablet.webp,
              @/assets/images/BusinessSolutions/business-solutions-hero-tablet@2x.webp 2x
            "
          />
          <img
            srcset="
              @/assets/images/BusinessSolutions/business-solutions-hero-mobile.png,
              @/assets/images/BusinessSolutions/business-solutions-hero-mobile@2x.webp 2x
            "
            alt="Two Electrify America charging stations under a canopy"
          />
        </picture>
      </template>
      <h2>Customized Solutions to Meet Your Needs</h2>
      <p>
        Electrify America is the largest open Hyper-Fast network in the U.S. and collaborates with major corporations to
        promote electric vehicle awareness across the nation. We manage the entire electrification process — from
        initial planning to ongoing maintenance — so you don’t have to.
      </p>
    </Hero>
    <div class="wrapper">
      <section class="l-one__col l-one__col--left-align l-one__col--no-side-padding">
        <h2>Your Business, Electrified by Experts</h2>
      </section>
    </div>
    <div class="wrapper">
      <section class="grid-2 grid-equalHeight">
        <div class="column grid-column-equalHeight">
          <section class="content">
            <div class="text">
              <h3 class="headline4">Public Electrify America Station Hosting</h3>
              <p>
                Join leaders in <strong>convenience</strong>, <strong>retail</strong>, and <strong>groceries</strong> on
                the Electrify America network at no cost.
              </p>
            </div>
            <router-link class="button" :to="{ name: 'real-estate-en' }">Host a Station</router-link>
            <DecorativeList>
              <li>Electrify America pays 100% of the upfront capital investment</li>
              <li>Become an industry leader supporting clean energy</li>
            </DecorativeList>
            <picture>
              <source
                media="(min-width: 994px )"
                srcset="
                  @/assets/images/BusinessSolutions/business-solutions-card1.png,
                  @/assets/images/BusinessSolutions/business-solutions-card1@2x.png 2x
                "
              />
              <img
                src="@/assets/images/BusinessSolutions/business-solutions-card1.png"
                alt="Electric vehicles charging at Electrify America stations"
              />
            </picture>
          </section>
        </div>
        <div class="column grid-column-equalHeight">
          <section class="content">
            <div class="text">
              <h3 class="headline4">Build an EV Charging Network for Your Business</h3>
              <p>
                Own a branded network like top <strong>convenience</strong>, <strong>retail</strong>, and
                <strong>utility providers</strong>.
              </p>
            </div>
            <router-link class="button" :to="{ name: 'own-a-network-en' }">Own a Network</router-link>
            <DecorativeList>
              <li>Station branding and pricing control</li>
              <li>Equipment ownership</li>
              <li>Custom site design to accommodate your program needs</li>
              <li>NEVI and applicable Federal, State, and Local Grant funding application support</li>
            </DecorativeList>
            <picture>
              <source
                media="(min-width: 769px )"
                srcset="
                  @/assets/images/BusinessSolutions/business-solutions-card2.png,
                  @/assets/images/BusinessSolutions/business-solutions-card2@2x.png 2x
                "
              />
              <img
                src="@/assets/images/BusinessSolutions/business-solutions-card2.png"
                alt="An Electrify America Commercial charger at a Costco"
              />
            </picture>
          </section>
        </div>
      </section>
    </div>
    <div class="wrapper p-0">
      <Card
        :bgColor="true"
        :isReverse="true"
        heading="Up to 80% of Any Build Costs Could Be Covered By NEVI"
        headlineLevel="h2"
        :maxWidth="1200"
      >
        <template #image>
          <picture>
            <source
              media="(min-width: 993px)"
              srcset="
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero.png,
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero@2x.webp 2x
              "
            />
            <source
              media="(min-width: 576px )"
              srcset="
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero-tablet.webp,
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero-tablet@2x.webp 2x
              "
            />
            <img
              srcset="
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero-mobile.webp,
                @/assets/images/BusinessSolutions/business-solutions-secondary-hero-mobile@2x.webp 2x
              "
              alt="An Electrify America charger in the desert under a canopy"
              @click="routingEvent('https://www.fhwa.dot.gov/bipartisan-infrastructure-law/nevi_formula_program.cfm')"
            />
          </picture>
        </template>
        <p>
          The NEVI Formula Program provides $5 billion funding to all 50 states and territories over five years to
          encourage states to build EV charging station networks along highway corridors, paving the way for more
          Americans to enjoy the benefits of EV driving.
        </p>
        <a class="button-link" target="_blank" href="/pdf/NEVI_102725.pdf"
          >Learn more about Nevi funding<arrow class="arrow"></arrow
        ></a>
      </Card>
    </div>
  </div>
</template>

<script>
import Hero from '@/components/Hero/Hero.vue';
import DecorativeList from '@/components/DecorativeList/DecorativeList.vue';
// import Carousel from '@/components/Carousel/Carousel.vue';
import Card from '@/components/Card/Card.vue';
import arrow from '@/assets/images/Global/arrow.svg';

export default {
  metaInfo: {
    title: 'EV Charging Stations for Business | Electrify America',
    meta: [
      {
        name: 'description',
        content:
          'Work with Electrify America to bring EV charging to your business. Our expertise helps you attract new customers and support EV drivers with a turnkey solution.',
      },
    ],
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/business-solutions/' }],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          url: 'https://www.electrifyamerica.com/business-solutions/',
          name: 'Electrify America Business Solutions',
          isPartOf: { '@id': 'https://www.electrifyamerica.com/#website' },
          mainEntity: {
            '@type': 'Service',
            name: 'Electrify America Business Charging Solutions',
            provider: { '@id': 'https://www.electrifyamerica.com/#organization' },
            areaServed: 'US',
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: 'Business Charging Programs',
              itemListElement: [
                {
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: 'Public Electrify America Station Hosting',
                    description:
                      'Electrify America pays 100 percent of the upfront capital investment to host a public charging station at your business.',
                  },
                },
                {
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: 'Public-Access Station Ownership',
                    description:
                      'Own a branded EV charging network with station branding, pricing control, and equipment ownership, supported by Electrify America.',
                  },
                },
              ],
            },
          },
        },
      },
    ],
  },
  components: { Hero, DecorativeList, Card, arrow },
  data() {
    return {
      isQa: process.env.VUE_APP_ENV === 'qa',
    };
  },
  computed: {
    isMobile() {
      return this.$resize && this.$mq.below(994);
    },
    isSmall() {
      return this.$resize && this.$mq.below(769);
    },
    isExtraSmall() {
      return this.$resize && this.$mq.below(576);
    },
  },
  methods: {
    routingEvent(url) {
      window.open(url, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.business-solutions {
  h2 {
    font-weight: $fw-light;
  }
  .headline4 {
    font-weight: $fw-medium;
    font-size: $global-font-size * 1.27;
  }

  ::v-deep .hero {
    &__text section {
      padding-top: 32px;
      h3 {
        font-weight: $fw-medium;
        font-size: 1.5rem;
      }
      @media (min-width: 750px) {
        max-width: 992px;
        display: flex;
        h3 {
          flex: 0.9;
          font-size: 2.1rem;
        }
        p {
          flex: 1.5;
          padding-left: 40px;
        }
      }
    }
  }
  ::v-deep .decorative-list {
    @media (min-width: $bp_medium_min) {
      height: 220px;
    }
    @media (min-width: 1080px) {
      height: 170px;
    }
    li {
      background-image: url('~@/assets/images/BusinessSolutions/checkmark.svg?external');
      padding-left: 1.75rem;
      font-weight: $fw-medium;
    }
  }
  ::v-deep .card {
    border-radius: 8px;
    @media (min-width: 768px) {
      margin: 0 auto;
      margin-top: 100px;
      margin-bottom: 80px;
      max-width: $max_width;
    }
    &__container {
      flex-direction: column;
      @media (min-width: $bp_large_min) {
        flex-direction: row;
      }
    }
    h2 {
      font-weight: $fw-medium;
    }
    &__column {
      max-width: none;
      @media (min-width: $bp_large_min) {
        max-width: 502px;
      }
    }
    img {
      max-width: 100%;
      border-radius: $global-border-radius;
      @media (min-width: $bp_large_min) {
        max-width: 360px;
      }
      &:hover {
        box-shadow: 0 20px 15px -2px #ccc;
        transition: box-shadow 0.1s ease-in-out;
      }
    }
  }

  .wrapper {
    @media (min-width: $bp_large_min) {
      padding: 0 $spacing-unit * 8;
    }
  }

  .grid-2 {
    @media (max-width: $bp_medium_min) {
      flex-direction: column;
      align-content: center;
    }
    .column {
      padding: 0 $spacing-unit * 1.5;
      @media (max-width: $bp_medium_min) {
        max-width: 700px !important;
      }
      .content {
        padding: $spacing-unit * 2;
        border-radius: 8px;
        box-shadow: 0 8px 24px 0 rgba(10, 18, 74, 0.1);
        border-top: 8px solid $c-primary;
        @media (max-width: $bp_medium_min) {
          max-width: 700px !important;
          margin-bottom: 40px;
        }
        .text {
          height: 164px;
        }
      }
    }
    img {
      width: 100%;
      margin-bottom: $global-spacing;
      border-radius: $global-border-radius;
      &:hover {
        box-shadow: 0 20px 15px -2px #ccc;
        transition: box-shadow 0.1s ease-in-out;
      }
    }
  }

  .button-link {
    margin-bottom: 42px;
  }
  .button {
    box-sizing: border-box;
    width: 100%;
    margin-top: 0;
    margin-bottom: 24px;
    padding: 1.28em 1rem;
  }
  .l-one__col {
    margin-top: $spacing-unit * 8;
    margin-bottom: $spacing-unit * 5;
  }
  .carousel {
    p {
      min-height: 96px;
    }
  }
  .decorative-list {
    font-size: 14px;
  }
  .mb-0 {
    margin-bottom: 0px;
  }
  @media (max-width: $bp_medium_min) {
    .p-0 {
      padding: 0;
    }
    .card {
      border-radius: 0px !important;
    }
  }
}
</style>
